import React, { useState, useEffect } from "react";
import {
    getSessions,
    revokeSession,
    revokeAllSessions,
    revokeOtherSessions
} from "../../api/authClient";

export default function SessionManagement() {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [revoking, setRevoking] = useState(null);

    useEffect(() => {
        fetchSessions();
    }, []);

    const fetchSessions = async () => {
        try {
            setLoading(true);
            setError("");
            const data = await getSessions();
            setSessions(data.sessions || []);
        } catch (err) {
            setError("Failed to load sessions");
        } finally {
            setLoading(false);
        }
    };

    const handleRevokeSession = async (sessionId) => {
        try {
            setRevoking(sessionId);
            setError("");
            await revokeSession(sessionId);
            setSuccess("Session revoked successfully");
            await fetchSessions();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to revoke session");
        } finally {
            setRevoking(null);
        }
    };

    const handleRevokeAllSessions = async () => {
        if (!window.confirm("This will log you out of all sessions including this one. Continue?")) {
            return;
        }
        try {
            setRevoking("all");
            setError("");
            await revokeAllSessions();
            // User will be logged out, page will redirect
            window.location.href = "/login";
        } catch (err) {
            setError(err.response?.data?.message || "Failed to revoke sessions");
            setRevoking(null);
        }
    };

    const handleRevokeOtherSessions = async () => {
        const currentSession = sessions.find(s => s.current);
        if (!currentSession) {
            setError("Could not identify current session");
            return;
        }
        try {
            setRevoking("others");
            setError("");
            const response = await revokeOtherSessions(currentSession.id);
            setSuccess(response.message || "Other sessions revoked");
            await fetchSessions();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to revoke other sessions");
        } finally {
            setRevoking(null);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Unknown";
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return "Just now";
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
        if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
        return date.toLocaleDateString();
    };

    const getDeviceIcon = (deviceInfo) => {
        const info = deviceInfo?.toLowerCase() || "";
        if (info.includes("mobile") || info.includes("iphone") || info.includes("android")) {
            return (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
            );
        }
        return (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
        );
    };

    if (loading) {
        return (
            <div className="card p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-gray-100 rounded w-1/3"></div>
                    <div className="h-20 bg-gray-100 rounded"></div>
                    <div className="h-20 bg-gray-100 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-accent-50 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-accent-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Active Sessions</h2>
                        <p className="text-sm text-gray-500">
                            {sessions.length} active session{sessions.length !== 1 ? "s" : ""}
                        </p>
                    </div>
                </div>
                {sessions.length > 1 && (
                    <button
                        onClick={handleRevokeOtherSessions}
                        disabled={revoking === "others"}
                        className="text-sm text-accent-600 hover:text-accent-700 disabled:opacity-50"
                    >
                        {revoking === "others" ? "Revoking..." : "Sign out other sessions"}
                    </button>
                )}
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-danger-500">{error}</p>
                </div>
            )}
            {success && (
                <div className="mb-4 p-3 bg-accent-50 border border-accent-200 rounded-lg">
                    <p className="text-sm text-accent-600">{success}</p>
                </div>
            )}

            <div className="space-y-3">
                {sessions.map((session) => (
                    <div
                        key={session.id}
                        className={`p-4 rounded-lg border ${
                            session.current
                                ? "bg-accent-50 border-accent-200"
                                : "bg-gray-50 border-gray-200"
                        }`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                                <div className="text-gray-500 mt-0.5">
                                    {getDeviceIcon(session.deviceInfo)}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-900">
                                            {session.deviceInfo || "Unknown Device"}
                                        </span>
                                        {session.current && (
                                            <span className="px-1.5 py-0.5 text-xs bg-accent-100 text-accent-600 rounded">
                                                Current
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                                        <p>
                                            {session.browser && session.operatingSystem
                                                ? `${session.browser} on ${session.operatingSystem}`
                                                : "Browser info unavailable"}
                                        </p>
                                        <p>IP: {session.ipAddress || "Unknown"}</p>
                                        <p>Last active: {formatDate(session.lastActiveAt)}</p>
                                    </div>
                                </div>
                            </div>
                            {!session.current && (
                                <button
                                    onClick={() => handleRevokeSession(session.id)}
                                    disabled={revoking === session.id}
                                    className="text-xs text-danger-500 hover:text-danger-600 disabled:opacity-50"
                                >
                                    {revoking === session.id ? "..." : "Revoke"}
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {sessions.length > 0 && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                    <button
                        onClick={handleRevokeAllSessions}
                        disabled={revoking === "all"}
                        className="text-sm text-danger-500 hover:text-danger-600 disabled:opacity-50"
                    >
                        {revoking === "all" ? "Signing out..." : "Sign out of all sessions"}
                    </button>
                    <p className="text-xs text-gray-500 mt-1">
                        This will log you out everywhere, including this device.
                    </p>
                </div>
            )}
        </div>
    );
}
